import React, { useState, useMemo } from "react";
import {
  View,
  StyleSheet,
  TextInput,
  FlatList,
  SafeAreaView,
} from "react-native";
import { useLikedNames } from "../context/LikedNamesContext";
import { useTranslation } from "../hooks/useTranslation";
import CustomText from "../components/CustomText";
import SearchNameItem from "../components/SearchNameItem";

// Import all name data
import eeuuMale from "../../data/eeuu-male.json";
import eeuuFemale from "../../data/eeuu-female.json";
import italyMale from "../../data/italy-male.json";
import italyFemale from "../../data/italy-female.json";
import spainMale from "../../data/spain-male.json";
import spainFemale from "../../data/spain-female.json";
import swedenMale from "../../data/sweden-male.json";
import swedenFemale from "../../data/sweden-female.json";
import ukMale from "../../data/uk-male.json";
import ukFemale from "../../data/uk-female.json";

type NameData = {
  nombre: string;
  significado_resumido: string;
  significado_extendido: string;
  origen: string;
  personajes_famosos: string[];
  ranking: number;
  country: string;
  sex: "male" | "female";
  uniqueId: string;
};

const SearchScreen = ({ navigation }: { navigation: any }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { likedNames, addLikedName, removeLikedNameExact } = useLikedNames();
  const { getTranslation } = useTranslation();

  // Utility function to normalize text by removing accents
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize("NFD") // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, "") // Remove accent marks
      .trim();
  };

  // Combine all name data with country and sex metadata
  const allNames = useMemo((): NameData[] => {
    const datasets = [
      { data: eeuuMale, country: "eeuu", sex: "male" as const },
      { data: eeuuFemale, country: "eeuu", sex: "female" as const },
      { data: italyMale, country: "italy", sex: "male" as const },
      { data: italyFemale, country: "italy", sex: "female" as const },
      { data: spainMale, country: "spain", sex: "male" as const },
      { data: spainFemale, country: "spain", sex: "female" as const },
      { data: swedenMale, country: "sweden", sex: "male" as const },
      { data: swedenFemale, country: "sweden", sex: "female" as const },
      { data: ukMale, country: "uk", sex: "male" as const },
      { data: ukFemale, country: "uk", sex: "female" as const },
    ];

    let globalIndex = 0;
    return datasets.flatMap(({ data, country, sex }) => {
      // Deduplicate names within the same country/sex combination
      // Use accent-insensitive comparison for deduplication
      const seen = new Set();
      const deduplicatedData = data.filter((name) => {
        const normalizedName = normalizeText(name.nombre);
        const key = `${country}-${sex}-${normalizedName}`;
        if (seen.has(key)) {
          return false; // Skip duplicate
        }
        seen.add(key);
        return true;
      });
      
      return deduplicatedData.map((name) => ({ 
        ...name, 
        country, 
        sex, 
        uniqueId: `${country}-${sex}-${globalIndex++}` 
      }));
    });
  }, []);

  // Filter names based on search query
  const filteredNames = useMemo(() => {
    if (searchQuery.length < 3) return [];
    
    const normalizedQuery = normalizeText(searchQuery);
    return allNames.filter((name) =>
      normalizeText(name.nombre).includes(normalizedQuery)
    );
  }, [searchQuery, allNames]);

  const isNameLiked = (name: NameData): boolean => {
    return likedNames.some(
      (likedName) =>
        likedName.nombre === name.nombre &&
        likedName.sex === name.sex &&
        likedName.country === name.country
    );
  };

  const handleLikeName = (name: NameData) => {
    const isLiked = isNameLiked(name);
    
    if (isLiked) {
      // Remove from liked names - exact match only (same name, sex, country)
      removeLikedNameExact(name.nombre, name.sex, name.country);
    } else {
      // Add to liked names - need to pass the correct structure
      const nameObject = {
        nombre: name.nombre,
        significado_resumido: name.significado_resumido,
        significado_extendido: name.significado_extendido,
        ranking: name.ranking.toString(),
        personajes_famosos: name.personajes_famosos,
      };
      addLikedName(nameObject, name.sex, name.country);
    }
  };

  const handleNamePress = (name: NameData) => {
    navigation.navigate("NameDetails", {
      nameDetails: {
        id: `${name.country}-${name.sex}-${name.nombre}`,
        nombre: name.nombre,
        significado_extendido: name.significado_extendido,
        significado_resumido: name.significado_resumido,
        personajes_famosos: name.personajes_famosos,
        ranking: name.ranking,
        sex: name.sex,
      },
    });
  };

  const renderSearchNameItem = ({ item }: { item: NameData }) => (
    <SearchNameItem
      name={item}
      isLiked={isNameLiked(item)}
      onLike={() => handleLikeName(item)}
      onPress={() => handleNamePress(item)}
    />
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder={getTranslation("searchPlaceholder")}
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoCapitalize="words"
          autoCorrect={false}
        />
      </View>

      {searchQuery.length > 0 && searchQuery.length < 3 && (
        <View style={styles.minCharsContainer}>
          <CustomText style={styles.minCharsText}>
            {getTranslation("searchMinChars")}
          </CustomText>
        </View>
      )}

      {searchQuery.length >= 3 && filteredNames.length === 0 && (
        <View style={styles.noResultsContainer}>
          <CustomText style={styles.noResultsText}>
            {getTranslation("searchNoResults")}
          </CustomText>
        </View>
      )}

      {filteredNames.length > 0 && (
        <View style={styles.resultsContainer}>
          <CustomText style={styles.resultsCount}>
            {getTranslation("searchResultsCount").replace(
              "{count}",
              filteredNames.length.toString()
            )}
          </CustomText>
          <FlatList
            data={filteredNames}
            renderItem={renderSearchNameItem}
            keyExtractor={(item) => item.uniqueId}
            showsVerticalScrollIndicator={false}
            style={styles.resultsList}
          />
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  searchContainer: {
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  searchInput: {
    height: 48,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 16,
    fontSize: 16,
    fontFamily: "Urbanist_400Regular",
    backgroundColor: "#FFFFFF",
  },
  minCharsContainer: {
    padding: 24,
    alignItems: "center",
  },
  minCharsText: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
  },
  noResultsContainer: {
    padding: 24,
    alignItems: "center",
  },
  noResultsText: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
  },
  resultsContainer: {
    flex: 1,
    padding: 16,
  },
  resultsCount: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 12,
    fontFamily: "Urbanist_700Bold",
  },
  resultsList: {
    flex: 1,
  },
});

export default SearchScreen;